import React from 'react';
import { render, screen } from '@testing-library/react';
import userEvent from '@testing-library/user-event';

import {
	mockResizeObserver,
	mockElementOffset,
	expectLog,
	keypressLegacy,
} from './utils';
import {
	KeyboardTester,
	DatePickerWithValue,
	DateRangePickerWithValue,
	TimePickerWithValue,
	SelectWithValue,
	FocusTrapTest,
	ModalTest,
	DrawerTest,
	DialogTest,
	PopoverTest,
	TakeoverTest,
	ActionMenuTest,
	SideNavTest,
	TabTest,
	ButtonToggleTest,
} from './components';

mockResizeObserver();
mockElementOffset();
window.HTMLElement.prototype.scrollIntoView = () => {};

describe('DatePicker', () => {
	beforeEach(() => jest.spyOn(console, 'error'));

	it('keyboard nav: {enter} on icon to open picker, navigation over calendar', async () => {
		const user = userEvent.setup();
		render(
			<KeyboardTester>
				<DatePickerWithValue />
			</KeyboardTester>
		);

		const cal = () => screen.getAllByRole('grid')[0] || global.document;

		await expectLog({ text: 'first focusable' });
		await user.tab();
		await expectLog({ text: 'toggle calendar' });
		await user.tab({ shift: true });
		await expectLog({ text: 'first focusable' });
		await user.tab();
		await expectLog({ text: 'toggle calendar' });
		await user.keyboard('{enter}'); // Opens calendar
		keypressLegacy(cal(), 'ArrowDown'); // Kendo relied on event.code, which is unsupported by user-events@v14
		keypressLegacy(cal(), 'ArrowDown');
		keypressLegacy(cal(), 'ArrowLeft');
		await expectLog({ text: '24' });
		keypressLegacy(cal(), 'Enter'); // Select value
		await expectLog({
			text: '11/24/2022',
			anvilComponent: 'DatePicker', // Just for example
		});
		await user.tab();
		await expectLog({ text: 'last focusable' });
	});

	it('keyboard nav: {arrowDown} on mask to open picker, navigation over calendar', async () => {
		const user = userEvent.setup();
		render(
			<KeyboardTester>
				<DatePickerWithValue />
			</KeyboardTester>
		);

		const cal = () => screen.getAllByRole('grid')[0] || global.document;

		await expectLog({ text: 'first focusable' });
		await user.tab();
		await expectLog({ text: 'toggle calendar' }); // Icon focused
		await user.tab();
		await expectLog({ text: '11/11/2022' }); // Input focused
		await user.keyboard('{ArrowDown}'); // Opens calendar
		await expectLog({ text: '11/11/2022' });
		await user.keyboard('{ArrowDown}');
		await expectLog({ text: '11' });
		keypressLegacy(cal(), 'ArrowDown'); // Kendo relied on event.code, which is unsupported by user-events@v14
		await expectLog({ text: '18' });
		keypressLegacy(cal(), 'ArrowRight');
		await expectLog({ text: '19' });
		keypressLegacy(cal(), 'Enter'); // Select value
		await expectLog({ text: '11/19/2022' });
		await user.tab();
		await expectLog({ text: 'last focusable' });
	});
});

describe('DateRangePicker', () => {
	const user = userEvent.setup();
	beforeEach(() => jest.spyOn(console, 'error'));

	it('keyboard nav: {enter} or {arrowDown} on mask to open picker, autofocus on start, tab to end date, tab to navigation over calendar', async () => {
		render(
			<KeyboardTester>
				<DateRangePickerWithValue />
			</KeyboardTester>
		);

		await expectLog({ text: 'first focusable' });
		await user.tab();
		await expectLog({ text: 'Nov 7, 2022 - Nov 15, 2022' });
		// TBD, write test here, as keyboard is not yet supported
		await user.tab();
		await expectLog({ text: 'clear selection' });
		await user.tab();
		await expectLog({ text: 'last focusable' });
	});
});

describe('TimePicker', () => {
	beforeEach(() => jest.spyOn(console, 'error'));

	it('keyboard nav: focus to open picker, {arrowDown} to navigate over picker, enter to select value', async () => {
		const user = userEvent.setup();
		render(
			<KeyboardTester>
				<TimePickerWithValue />
			</KeyboardTester>
		);

		await expectLog({ text: 'first focusable' });
		await user.tab();
		await expectLog({ text: '08:00 AM' });
		await user.keyboard('{ArrowDown}');
		await expectLog({ text: '08:00 AM' });
		await user.keyboard('{ArrowDown}');
		await expectLog({ text: '08:30 AM' });
		await user.keyboard('{ArrowDown}');
		await expectLog({ text: '09:00 AM' });
		await user.keyboard('{Enter}');
		await expectLog({
			text: '09:00 AM', // new value selected
			anvilComponent: 'TimePicker',
		});
		await user.tab();
		await expectLog({ text: 'last focusable' });
		await user.tab({ shift: true });
		await expectLog({ text: '09:00 AM' });
	});

	it('keyboard access: typing "123" should inject "01:23 AM" option (between stepped options), tab should select it', async () => {
		const user = userEvent.setup();
		render(
			<KeyboardTester>
				<TimePickerWithValue />
			</KeyboardTester>
		);

		await expectLog({ text: 'first focusable' });
		await user.tab();
		await expectLog({ text: '08:00 AM' });
		await user.keyboard('123{Tab}'); // Selects "01:23 AM" option
		await expectLog({ text: 'last focusable' });
		await user.tab({ shift: true });
		await expectLog({ text: '01:23 AM' }); // "01:23 AM" is selected
	});
	it('keyboard access: typing "22" should return no option as "10:00 PM" is restricted by max prop, tab should select nothing', async () => {
		const user = userEvent.setup();
		render(
			<KeyboardTester>
				<TimePickerWithValue />
			</KeyboardTester>
		);

		await expectLog({ text: 'first focusable' });
		await user.tab();
		await expectLog({ text: '08:00 AM' });
		await user.keyboard('22{Tab}'); // Should propose no option to select due to min-max restriction
		await expectLog({ text: 'last focusable' });
		await user.tab({ shift: true });
		await expectLog({ text: 'HH:MM AM' }); // No value was selected
	});
});

describe('AnvilSelect', () => {
	beforeEach(() => jest.spyOn(console, 'error'));

	it('keyboard nav: {arrowDown} to open picker, {arrowDown} to navigate over picker, enter to select value', async () => {
		const user = userEvent.setup();
		render(
			<KeyboardTester>
				<SelectWithValue />
			</KeyboardTester>
		);

		await expectLog({ text: 'first focusable' });
		await user.tab();
		await expectLog({ text: 'Select a technician' });
		await user.keyboard('{ArrowDown}');
		await expectLog({ text: 'Bob Ross' });
		await user.keyboard('{ArrowDown}');
		await expectLog({
			text: 'Jackie Robinson',
			anvilComponent: 'Checkbox',
		});
		await user.keyboard('{Enter}');
		await expectLog({
			text: 'Jackie Robinson', // new value selected
			anvilComponent: 'AnvilSelect',
		});
		await user.tab();
		await expectLog({ text: 'clear selection' });
		await user.tab();
		await expectLog({ text: 'last focusable' });
	});
});

describe('ActionMenu', () => {
	beforeEach(() => jest.spyOn(console, 'error'));

	it.skip('keyboard nav: arrow keys moves focus, {Enter}, {ArrowRight} opens submenu, {ArrowLeft} closes, {Escape} closes all menus', async () => {
		const user = userEvent.setup();
		render(
			<KeyboardTester>
				<ActionMenuTest />
			</KeyboardTester>
		);

		await expectLog({ text: 'first focusable' });
		await user.tab();
		await expectLog({ text: 'Open' });
		await user.keyboard('{Enter}'); // Render ActionMenu
		await expectLog({ text: 'Item 1' }); // Focus is inside
		await user.keyboard('{ArrowDown}');
		await expectLog({ text: 'Item 2 (cascading)' }); // Focus is moving
		await user.keyboard('{ArrowRight}');
		await expectLog({ text: 'Item 21' }); // Submenu opened
		await user.keyboard('{ArrowDown}{ArrowDown}{ArrowDown}');
		await expectLog({ text: 'Item 24' }); // Last item focused
		await user.keyboard('{ArrowDown}');
		await expectLog({ text: 'Item 21' }); // Looping
		await user.keyboard('{ArrowLeft}');
		await expectLog({ text: 'Item 2 (cascading)' }); // Submenu closed
		await user.keyboard('{ }'); // Submenu opened on Space keypress
		await expectLog({ text: 'Item 21' });
		await user.keyboard('{ArrowLeft}');
		await expectLog({ text: 'Item 2 (cascading)' }); // Submenu closed
		await user.keyboard('{ArrowDown}');
		await expectLog({ text: 'Item 3' }); // Focus is moving
		await user.keyboard('{ArrowDown}');
		await expectLog({ text: 'Item 4' });
		await user.keyboard('{ArrowDown}');
		await expectLog({ text: 'Item 1' }); // Looping
		await user.keyboard('{Escape}');
		await expectLog({ text: 'Open' }); // Menu closed
		await user.keyboard('{Enter}{ArrowDown}{ArrowRight}{ArrowDown}');
		await expectLog({ text: 'Item 22 (cascading)' });
		await user.keyboard('{Escape}');
		await expectLog({ text: 'Open' }); // Escape to close all the menus
		await user.keyboard('{Enter}');
		await user.tab({ shift: true });
		await expectLog({ text: 'Open' }); // Focus not locked, shift+tab sends focus back to the trigger
		await user.keyboard('{ }');
		await expectLog({ text: 'Item 1' }); // Space to open the menu and focus first item
		await user.keyboard('{ArrowDown}');
		await expectLog({ text: 'Item 2 (cascading)' });
		await user.keyboard('{ArrowDown}');
		await expectLog({ text: 'Item 3' });
	});
});

describe('SideNav', () => {
	beforeEach(() => jest.spyOn(console, 'error'));

	it('keyboard nav: {Enter}, {Space} should make selection', async () => {
		const user = userEvent.setup();
		render(
			<KeyboardTester>
				<SideNavTest />
			</KeyboardTester>
		);

		await expectLog({ text: 'first focusable' });
		await user.tab();
		await user.tab();
		await expectLog({ text: 'Item 21' }); // Focus on selected item
		await user.keyboard('{ArrowDown}');
		await expectLog({ text: 'Item 22' });
		await user.keyboard('{Enter}'); // Makes selection
		await expectLog({ text: 'Item 22' });
		await user.keyboard('{ArrowDown}');
		await expectLog({ text: 'Item 23' });
		await user.tab();
		await expectLog({ text: 'last focusable' });
		await user.tab({ shift: true });
		await expectLog({ text: 'Item 22' }); // Focus back on selected
		await user.tab({ shift: true });
		await user.tab({ shift: true });
		await expectLog({ text: 'first focusable' });
		await user.tab();
		await user.tab();
		await expectLog({ text: 'Item 22' }); // Focus back on selected
		await user.keyboard('{ArrowDown}');
		await user.keyboard('{ }'); // {Space} make selection
		await user.tab();
		await user.tab({ shift: true });
		await expectLog({ text: 'Item 23' }); // Focus back on selected
	});

	it('keyboard nav: arrow keys should navigate over SideNav, {Enter}, {ArrowLeft}, {ArrowRight} should toggle submenu', async () => {
		const user = userEvent.setup();
		render(
			<KeyboardTester>
				<SideNavTest />
			</KeyboardTester>
		);

		await expectLog({ text: 'first focusable' });
		await user.tab();
		await user.tab();
		await expectLog({ text: 'Item 21' }); // Focus on selected item
		await user.keyboard('{ArrowDown}');
		await expectLog({ text: 'Item 22' });
		await user.keyboard('{ArrowLeft}');
		await expectLog({ text: 'Item 2' }); // Left should move focus on collapsible parent
		await user.keyboard('{ArrowLeft}');
		await expectLog({ text: 'Item 2' }); // Closes collapsible
		await user.keyboard('{ArrowLeft}');
		await expectLog({ text: 'Item 2' }); // Should do nothing
		await user.keyboard('{ArrowDown}');
		await expectLog({ text: 'Item 3' }); // Collapsible is really closed
		await user.keyboard('{ArrowUp}');
		await user.keyboard('{Enter}'); // Opens collapsible
		await user.keyboard('{ArrowDown}');
		await expectLog({ text: 'Item 21' }); // Nested
		await user.keyboard('{ArrowDown}{ArrowDown}{ArrowDown}');
		await expectLog({ text: 'Item 3' });
		await user.keyboard('{ArrowDown}');
		await expectLog({ text: 'Item 4' });
		await user.keyboard('{ArrowDown}');
		await expectLog({ text: 'Item 4' }); // Looping is disabled
		await user.keyboard('{Home}');
		await expectLog({ text: 'Item 1' }); // Home works
		await user.keyboard('{End}');
		await expectLog({ text: 'Item 4' }); // End works
		await user.keyboard('{Home}');
		await user.keyboard('{ArrowDown}');
		await user.keyboard('{Enter}'); // Closes collapsible
		await user.keyboard('{ArrowRight}'); // Opens collapsible
		await expectLog({ text: 'Item 2' }); // Collapsible is opened
		await user.keyboard('{ArrowRight}');
		await expectLog({ text: 'Item 21' });
		await user.keyboard('{ArrowDown}');
		await expectLog({ text: 'Item 22' });
		await user.tab();
		await expectLog({ text: 'last focusable' });
	});
});

describe('Tab', () => {
	beforeEach(() => jest.spyOn(console, 'error'));

	it('keyboard nav: arrow keys should move focus, {Enter} or {Space} should activate Tab', async () => {
		const user = userEvent.setup();
		render(
			<KeyboardTester>
				<TabTest />
			</KeyboardTester>
		);

		await expectLog({ text: 'first focusable' });
		await user.tab();
		await expectLog({ text: 'Tab 2' }); // Focus on selected item
		await user.keyboard('{ArrowRight}');
		await expectLog({ text: 'Tab 3' });
		await user.keyboard('{ArrowRight}');
		await expectLog({ text: 'Tab 1' }); // Looping works
		await user.keyboard('{ArrowLeft}');
		await expectLog({ text: 'Tab 3' });
		await user.keyboard('{ArrowRight}{ArrowRight}');
		await expectLog({ text: 'Tab 2' });
		await user.tab();
		await expectLog({ text: 'Action' });
		await user.tab();
		await expectLog({ text: 'last focusable' });
		await user.tab({ shift: true });
		await expectLog({ text: 'Action' });
		await user.tab({ shift: true });
		await expectLog({ text: 'Tab 2' }); // Focus back on selected
		await user.keyboard('{ArrowRight}');
		await expectLog({ text: 'Tab 3' });
		await user.keyboard('{ }'); // {Space} make selection
		await expectLog({ text: 'Tab 3' });
		await user.tab({ shift: true });
		await expectLog({ text: 'first focusable' });
		await user.tab();
		await expectLog({ text: 'Tab 3' }); // Focus back
		await user.keyboard('{ArrowUp}');
		await expectLog({ text: 'Tab 3' }); // Do nothing
		await user.keyboard('{ArrowDown}');
		await expectLog({ text: 'Tab 3' }); // Do nothing
	});
});

describe('ButtonToggle', () => {
	beforeEach(() => jest.spyOn(console, 'error'));

	it('keyboard nav: arrow left and right keys should move selection', async () => {
		const user = userEvent.setup();
		render(
			<KeyboardTester>
				<ButtonToggleTest />
			</KeyboardTester>
		);

		await expectLog({ text: 'first focusable' });
		await user.tab();
		await expectLog({ text: 'Item 2' }); // Focus on selected item
		await user.keyboard('{ArrowRight}');
		await expectLog({ text: 'Item 3' });
		await user.keyboard('{ArrowRight}');
		await expectLog({ text: 'Item 1' }); // Loop from 3 to 1, skipping disabled (4)
		await user.keyboard('{ArrowRight}');
		await expectLog({ text: 'Item 2' });
		await user.keyboard('{ArrowLeft}');
		await expectLog({ text: 'Item 1' });
		await user.keyboard('{ArrowDown}');
		await expectLog({ text: 'Item 1' }); // Nothing happens
		await user.tab();
		await expectLog({ text: 'last focusable' });
		await user.tab({ shift: true });
		await expectLog({ text: 'Item 1' }); // Focus back
		await user.keyboard('{ArrowRight}');
		await expectLog({ text: 'Item 2' });
		await user.tab({ shift: true });
		await expectLog({ text: 'first focusable' });
		await user.tab();
		await expectLog({ text: 'Item 2' }); // Focus back
	});
});

describe('useFocusTrap', () => {
	beforeEach(() => jest.spyOn(console, 'error'));

	it('useFocusTrap: on any sequence of {tab} or {shift}{tab} focus should stay inside', async () => {
		const user = userEvent.setup();
		render(
			<KeyboardTester>
				<FocusTrapTest />
			</KeyboardTester>
		);

		await expectLog({ text: 'first focusable' });
		await user.tab();
		await expectLog({ text: 'Inside 1' });
		await user.tab();
		await expectLog({ text: 'Inside 2' });
		await user.tab();
		await expectLog({ text: 'Inside 3 (shard)' });
		await user.tab();
		await expectLog({ text: 'Inside 1' }); // Focus is trapped

		// Reversed tabbing
		await user.tab({ shift: true });
		await expectLog({ text: 'Inside 3 (shard)' });
		await user.tab({ shift: true });
		await expectLog({ text: 'Inside 2' });
		await user.tab({ shift: true });
		await expectLog({ text: 'Inside 1' });
		await user.tab({ shift: true });
		await expectLog({ text: 'Inside 3 (shard)' }); // Focus is trapped
	});

	it('Modal: focus trap, auto focus and restore focus should work inside Modal', async () => {
		const user = userEvent.setup();
		render(
			<KeyboardTester>
				<ModalTest />
			</KeyboardTester>
		);

		await expectLog({ text: 'first focusable' });
		await user.tab();
		await expectLog({ text: 'Open' });
		await user.keyboard('{Enter}');
		await expectLog({ text: 'Modal' }); // Auto focus on Modal's container
		await user.tab();
		await expectLog({ text: 'close modal' });
		await user.tab();
		await expectLog({ text: 'Inside 1' });
		await user.tab();
		await expectLog({ text: 'Inside 2' });
		await user.tab();
		await expectLog({ text: 'close modal' }); // Focus is trapped
		await user.keyboard('{Enter}');
		await expectLog({ text: 'Open' }); // Focus is restored
		await user.tab({ shift: true });
		await expectLog({ text: 'first focusable' });
	});

	it('Drawer: focus trap, auto focus and restore focus should work inside Drawer', async () => {
		const user = userEvent.setup();
		render(
			<KeyboardTester>
				<DrawerTest />
			</KeyboardTester>
		);

		await expectLog({ text: 'first focusable' });
		await user.tab();
		await expectLog({ text: 'Open' });
		await user.keyboard('{Enter}');
		await expectLog({ text: 'Drawer' }); // Auto focus on Drawer's container
		await user.tab();
		await expectLog({ text: 'close drawer' });
		await user.tab();
		await expectLog({ text: 'Inside 1' });
		await user.tab();
		await expectLog({ text: 'Inside 2' });
		await user.tab();
		await expectLog({ text: 'close drawer' }); // Focus is trapped
		await user.keyboard('{Enter}');
		await expectLog({ text: 'Open' }); // Focus is restored
		await user.tab({ shift: true });
		await expectLog({ text: 'first focusable' });
	});

	it('Dialog: focus trap, auto focus and restore focus: should work inside Dialog', async () => {
		const user = userEvent.setup();
		render(
			<KeyboardTester>
				<DialogTest />
			</KeyboardTester>
		);

		await expectLog({ text: 'first focusable' });
		await user.tab();
		await expectLog({ text: 'Open' });
		await user.keyboard('{Enter}');
		await expectLog({ text: 'DialogClose' }); // Auto focus on Dialog's container
		await user.tab();
		await expectLog({ text: 'Inside 1' });
		await user.tab();
		await expectLog({ text: 'Inside 2' });
		await user.tab();
		await expectLog({ text: 'Close' });
		await user.tab();
		await expectLog({ text: 'Inside 1' }); // Focus is trapped
		await user.tab({ shift: true });
		await expectLog({ text: 'Close' });
		await user.keyboard('{Enter}');
		await expectLog({ text: 'Open' }); // Focus is restored
		await user.tab({ shift: true });
		await expectLog({ text: 'first focusable' });
	});

	it('Popover: focus trap, auto focus and restore focus should work inside Popover', async () => {
		const user = userEvent.setup();
		render(
			<KeyboardTester>
				<PopoverTest />
			</KeyboardTester>
		);

		await expectLog({ text: 'first focusable' });
		await user.tab();
		await expectLog({ text: 'Open' });
		await user.keyboard('{Enter}');
		await expectLog({ text: 'Inside 1' });
		await user.tab();
		await expectLog({ text: 'Inside 2' });
		await user.tab();
		await expectLog({ text: 'Close' });
		await user.tab();
		await expectLog({ text: 'Inside 1' }); // Focus is trapped
		await user.tab({ shift: true });
		await expectLog({ text: 'Close' });
		await user.keyboard('{Enter}');
		await expectLog({ text: 'Open' }); // Focus is restored
		await user.tab({ shift: true });
		await expectLog({ text: 'first focusable' });
	});

	it('Takeover: focus trap, auto focus and restore focus should work inside Takeover', async () => {
		const user = userEvent.setup();
		render(
			<KeyboardTester>
				<TakeoverTest />
			</KeyboardTester>
		);

		await expectLog({ text: 'first focusable' });
		await user.tab();
		await expectLog({ text: 'Open' });
		await user.keyboard('{Enter}');
		await expectLog({ text: 'Takeover' }); // Auto focus on Takeover's container
		await user.tab();
		await expectLog({ text: 'close takeover' });
		await user.tab();
		await expectLog({ text: 'Inside 1' });
		await user.tab();
		await expectLog({ text: 'Inside 2' });
		await user.tab();
		await expectLog({ text: 'close takeover' }); // Focus is trapped
		await user.keyboard('{Enter}');
		await expectLog({ text: 'Open' }); // Focus is restored
		await user.tab({ shift: true });
		await expectLog({ text: 'first focusable' });
	});
});
